import axios from 'axios';
import { getServerUrl } from './utils/serverUrl';

// Global axios interceptor: rewrite baked REACT_APP_BACKEND_URL to runtime server URL.
// This lets users point the app at their own self-hosted backend without a rebuild.
const BUILD_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
axios.interceptors.request.use((config) => {
  try {
    if (typeof config.url === 'string') {
      const runtime = getServerUrl();
      if (runtime && BUILD_URL && runtime !== BUILD_URL && config.url.startsWith(BUILD_URL)) {
        config.url = runtime + config.url.slice(BUILD_URL.length);
      }
    }
  } catch {}
  return config;
});

// Transient-503 auto-retry.
// The backend returns 503 when MongoDB is temporarily unreachable
// (Atlas failover, connection blip, etc.). These are recoverable in
// well under a second — retrying once is strictly better UX than
// bouncing the user out to a login screen. We bound this to a single
// retry so a true outage still fails fast.
axios.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error?.config;
    if (
      cfg &&
      !cfg.__gsRetried503 &&
      error?.response?.status === 503
    ) {
      cfg.__gsRetried503 = true;
      // Respect a Retry-After header if the server sent one, capped
      // at 2s so we never hang the UI.
      const ra = Number(error.response.headers?.['retry-after']);
      const waitMs = Math.min(2000, Math.max(250, Number.isFinite(ra) ? ra * 1000 : 400));
      await new Promise((res) => setTimeout(res, waitMs));
      return axios(cfg);
    }
    return Promise.reject(error);
  },
);

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LoginPage, RegisterPage, ForgotPasswordPage } from './pages/AuthPages';
import { CheckEmailPage, VerifyEmailPage } from './pages/EmailVerificationPages';
import { WaitlistPage } from './pages/WaitlistPage';
import { WaitlistStatusPage } from './pages/WaitlistStatusPage';
import { ManifestoPage } from './pages/ManifestoPage';
import { SelfHostPage } from './pages/SelfHostPage';
import { SealedSenderDemoPage } from './pages/SealedSenderDemoPage';
import { HaloLandingPage } from './pages/HaloLandingPage';
import { TransparencyPage } from './pages/TransparencyPage';
import { LegalPage } from './pages/LegalPage';
import { LandingPage } from './pages/LandingPage';
import { EmailProvidersHelpPage } from './pages/EmailProvidersHelpPage';
import { AdminHelpFeedbackPage } from './pages/AdminHelpFeedbackPage';
import { Dashboard } from './pages/Dashboard';
import { Toaster } from './components/ui/sonner';
import { DecoyModeWrapper, useDecoyMode } from './components/DecoyMode';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#05050A' }}>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Defense in depth: a logged-in-but-unverified session should never
  // reach the app surface. Backend also gates every /api call, but
  // the UX is nicer if the frontend routes proactively.
  if (user.email_verified === false) {
    return <Navigate to={`/check-email?email=${encodeURIComponent(user.email || '')}`} replace />;
  }

  return children;
};

// Public Route Component (redirects to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#05050A' }}>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/app" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        } 
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route path="/check-email" element={<CheckEmailPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route
        path="/waitlist"
        element={
          <PublicRoute>
            <WaitlistPage />
          </PublicRoute>
        }
      />
      <Route
        path="/waitlist/status/:token"
        element={<WaitlistStatusPage />}
      />
      <Route path="/manifesto" element={<ManifestoPage />} />
      <Route path="/selfhost" element={<SelfHostPage />} />
      <Route path="/privacy/sealed-sender-demo" element={<SealedSenderDemoPage />} />
      <Route path="/halo" element={<HaloLandingPage />} />
      <Route path="/transparency" element={<TransparencyPage />} />
      <Route path="/help/email-providers" element={<EmailProvidersHelpPage />} />
      <Route
        path="/admin/help-feedback"
        element={
          <ProtectedRoute>
            <AdminHelpFeedbackPage />
          </ProtectedRoute>
        }
      />
      <Route path="/legal" element={<LegalPage />} />
      <Route path="/legal/terms" element={<LegalPage />} />
      <Route path="/legal/privacy" element={<LegalPage />} />
      <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
      <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
      <Route 
        path="/" 
        element={<LandingPage />} 
      />
      <Route 
        path="/app" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Main App with Decoy Mode
function AppContent() {
  const { isDecoyActive, exitDecoy } = useDecoyMode('1337');
  
  return (
    <DecoyModeWrapper 
      isDecoyActive={isDecoyActive} 
      onExitDecoy={exitDecoy}
      exitCode="1337"
    >
      <AppRoutes />
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#1A1D24',
            border: '1px solid #272A30',
            color: '#F8FAFC',
          },
        }}
      />
    </DecoyModeWrapper>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
